/* eslint-disable no-console */

import { datadogLogs } from "@datadog/browser-logs";

const isClient = typeof window !== "undefined";
const isServer = !isClient;
const env =
  isClient && window.location.hostname.endsWith("justhome.com")
    ? "production"
    : "development";

const SCRIPT_BLACKLIST = ["iframe_resize.js"];

export type LoggerOptions = {
  service: string;
  version?: string;
  datadogDisabled?: boolean;
};

class Logger {
  private datadogDisabled: boolean = false;

  private _isBlacklistedScript(log: any): boolean {
    const message = log.message || "";
    const stack = log.error?.stack || "";
    const source = log.error?.source || "";
    const filename = log.error?.filename || "";

    const errorText = `${message} ${stack} ${source} ${filename}`.toLowerCase();

    return SCRIPT_BLACKLIST.some((script) =>
      errorText.includes(script.toLowerCase()),
    );
  }

  init({ service, version, datadogDisabled }: LoggerOptions) {
    if (datadogDisabled) {
      this.datadogDisabled = true;
      return;
    }

    datadogLogs.init({
      clientToken: "pub94c6cb288aceacadfca91f77ecd90e97",
      site: "datadoghq.eu",
      service: `frontend:${service}`,
      env,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      version,
      beforeSend: (log) => !this._isBlacklistedScript(log),
    });
  }

  error(message: string, error: Error | unknown, context?: object): void {
    if (isServer || (isClient && env === "development")) {
      if (context) {
        console.error(context);
      }
      console.error(message);

      if (error) {
        console.error(error);
      }
    }

    if (error instanceof DOMException && error.ABORT_ERR) {
      return;
    }

    if (isClient && !this.datadogDisabled) {
      const err = this._unknownToError(error);
      datadogLogs.logger.error(message, context, err);
    }
  }

  warn(message: string) {
    if (isClient && !this.datadogDisabled) {
      datadogLogs.logger.warn(message);
    }

    if (isServer || (isClient && env === "development")) {
      console.warn(message);
    }
  }

  info(message: string) {
    if (isClient && !this.datadogDisabled) {
      datadogLogs.logger.info(message);
    }

    if (isServer || (isClient && env === "development")) {
      console.info(message);
    }
  }

  private _unknownToError(error: Error | unknown): Error {
    if (!error) {
      return new Error("unknown error");
    }

    if (error instanceof Error) {
      return error;
    }

    if (typeof error === "string") {
      return new Error(error);
    }

    if (
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      return new Error(error.message);
    }

    return new Error(String(error));
  }
}

const logger = new Logger();

export default logger;
